import React, { memo } from 'react';

import { Typography } from 'antd';
import moment from 'moment';
import { PiPaperclip } from 'react-icons/pi';

import CustomReadMoreContent
  from '../../../../components/CustomReadMoreText/CustomReadMoreContent';
import CustomTag from '../../../../components/CustomTag/CustomTag';
import ProfileItem from '../../../../components/ProfileItem/ProfileItem';
import { useTheme } from '../../../../context/ThemeProvider';
import {
  DEFAULT_AVATAR,
  DELETED_USER,
} from '../../../../shared/data';
import { getSingularOrPlural } from '../../../../shared/utils';
import {
  ITQServiceRequestResponse,
  ITQServiceRequestResponseDetailsUser,
} from '../../../../types/tqConnectTypes/service-requests';
import TQConnectIcon from '../TQConnectIcon';

interface Props {
  isMyResponse?: boolean;
  response: ITQServiceRequestResponse;
  respondedBy: ITQServiceRequestResponseDetailsUser;
  canView?: boolean;
  onViewResponse?: () => void;
}

const ResponseCard: React.FC<Props> = ({
  isMyResponse,
  response,
  respondedBy,
  canView,
  onViewResponse,
}) => {
  const { colors: COLORS } = useTheme();

  const { description, attachments, createdAt, status } = response;

  const { name, profilePicUrl, tqConnectScore, tqReviewReceived } =
    respondedBy || {};

  return (
    <div className={`response-card ${isMyResponse ? 'response-card--my' : ''}`}>
      <ProfileItem
        className="card__header"
        avatar={profilePicUrl || DEFAULT_AVATAR}
        avatarSize={40}
        title={name || DELETED_USER}
        subtitle={
          typeof tqConnectScore === 'number' ? (
            <>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: COLORS.TEXT2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                <TQConnectIcon size={20} /> {tqConnectScore}
              </span>

              {tqReviewReceived ? (
                <>
                  <span>•</span>
                  <span>
                    {tqReviewReceived}{' '}
                    {getSingularOrPlural(tqReviewReceived, 'request')} completed
                  </span>
                </>
              ) : null}
            </>
          ) : null
        }
        action={
          status === 'rejected' ? (
            <CustomTag
              color="error-fill"
              style={{
                marginRight: 0,
              }}>
              Rejected
            </CustomTag>
          ) : null
        }
        titleStyle={{ marginBottom: 2, fontSize: 14 }}
        subtitleStyle={{
          width: '100%',
          fontSize: 12,
          // color: COLORS.TEXT_TITLE,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
        actionStyle={{
          alignSelf: 'start',
        }}
        style={{
          flexShrink: 0,
          padding: 8,
          paddingBottom: isMyResponse || canView ? 0 : 8,
        }}
      />

      {isMyResponse || canView ? (
        <>
          <CustomReadMoreContent
            lines={2}
            className="card__description"
            expandable={false}>
            {description}
          </CustomReadMoreContent>

          <div className="card__stats">
            <Typography.Text type="secondary" className="stats__time">
              {moment(createdAt).fromNow()}
            </Typography.Text>

            {attachments && attachments?.length > 0 && (
              <div className="stats__attachments">
                <PiPaperclip size={16} color={COLORS.ICON} />
                {getSingularOrPlural(attachments.length, 'Attachment')} (
                {attachments.length})
              </div>
            )}
          </div>

          <div className="card__action">
            <Typography.Text className="action__text" onClick={onViewResponse}>
              View response
            </Typography.Text>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default memo(ResponseCard);
